import React from 'react';

/**
 * Recipe Rack mark — three hairline shelves with jars / cheese / utensils /
 * plates on them, one accent piece per logo. Ported from the Claude Design
 * handoff bundle (`reciperack/project/lib/logo.js`).
 *
 * The original imperatively built an SVG string and dropped it into a
 * container. Here we return JSX directly so React can manage the tree.
 */

type ShelfStyle = 'jars' | 'cheese' | 'utensils' | 'plates' | 'mixed';
type Variant = 'default' | 'reverse' | 'icon' | 'monoaccent';

interface RRLogoProps {
  shelf?: ShelfStyle;
  /** Accent (tomato) colour for the one highlighted item. */
  accent?: string;
  /** Primary fill for all non-accent items + shelves. */
  fill?: string;
  /** Stroke colour for shelves + optional frame outline. */
  stroke?: string;
  frame?: 'rounded' | 'none';
  frameFill?: string | null;
  frameStroke?: boolean;
  variant?: Variant;
  size?: number | string;
}

interface JarItem { fn: 'jar'; x: number; w: number; h: number; lid: boolean; accent: boolean }
interface CheeseItem { fn: 'cheese'; x: number; r: number; holes: boolean; accent: boolean }
interface PotItem { fn: 'pot'; x: number; w: number; h: number; accent: boolean }
interface PlateItem { fn: 'plate'; x: number; w: number; accent: boolean }
interface BowlItem { fn: 'bowl'; x: number; w: number; accent: boolean }
interface BottleItem { fn: 'bottle'; x: number; h: number; accent: boolean }
type Item = JarItem | CheeseItem | PotItem | PlateItem | BowlItem | BottleItem;

const VB = 40;
const SHELVES = [14, 22, 30];

const STYLES: Record<ShelfStyle, { shelves: Item[][] }> = {
  jars: {
    shelves: [
      [
        { fn: 'jar', x: 10.5, w: 3, h: 5, lid: true, accent: false },
        { fn: 'jar', x: 15, w: 2.5, h: 4, lid: false, accent: false },
        { fn: 'jar', x: 21.5, w: 3.5, h: 5.5, lid: true, accent: true },
        { fn: 'jar', x: 27, w: 2.5, h: 4, lid: false, accent: false },
      ],
      [
        { fn: 'jar', x: 10.5, w: 3.5, h: 4, lid: true, accent: false },
        { fn: 'jar', x: 15, w: 2.5, h: 5, lid: false, accent: false },
        { fn: 'jar', x: 20, w: 3, h: 3.5, lid: true, accent: false },
        { fn: 'jar', x: 24.5, w: 3.5, h: 5, lid: false, accent: false },
        { fn: 'jar', x: 29.5, w: 2.5, h: 3.5, lid: true, accent: false },
      ],
      [
        { fn: 'plate', x: 14, w: 9, accent: false },
        { fn: 'jar', x: 23, w: 3, h: 4, lid: true, accent: false },
        { fn: 'plate', x: 29, w: 5, accent: false },
      ],
    ],
  },
  cheese: {
    shelves: [
      [
        { fn: 'cheese', x: 11, r: 2.6, holes: true, accent: false },
        { fn: 'cheese', x: 17, r: 1.8, holes: false, accent: false },
        { fn: 'cheese', x: 23, r: 2.8, holes: true, accent: true },
        { fn: 'cheese', x: 29, r: 2.0, holes: false, accent: false },
      ],
      [
        { fn: 'cheese', x: 10.5, r: 2.2, holes: false, accent: false },
        { fn: 'cheese', x: 16, r: 2.6, holes: true, accent: false },
        { fn: 'cheese', x: 22, r: 1.6, holes: false, accent: false },
        { fn: 'cheese', x: 27, r: 2.4, holes: true, accent: false },
        { fn: 'cheese', x: 32, r: 1.4, holes: false, accent: false },
      ],
      [
        { fn: 'plate', x: 14, w: 9, accent: false },
        { fn: 'cheese', x: 23, r: 1.6, holes: false, accent: false },
        { fn: 'plate', x: 29, w: 5, accent: false },
      ],
    ],
  },
  utensils: {
    shelves: [
      [
        { fn: 'pot', x: 11, w: 4, h: 4.5, accent: false },
        { fn: 'jar', x: 16.5, w: 2.5, h: 4, lid: true, accent: false },
        { fn: 'pot', x: 22.5, w: 4.5, h: 5.2, accent: true },
        { fn: 'jar', x: 28.5, w: 2.5, h: 4, lid: false, accent: false },
      ],
      [
        { fn: 'jar', x: 10.5, w: 3.5, h: 4, lid: true, accent: false },
        { fn: 'bottle', x: 15.5, h: 5, accent: false },
        { fn: 'jar', x: 19.5, w: 2.5, h: 3.5, lid: false, accent: false },
        { fn: 'pot', x: 24, w: 3.5, h: 4.5, accent: false },
        { fn: 'bottle', x: 29.5, h: 5.4, accent: false },
      ],
      [
        { fn: 'plate', x: 13, w: 8, accent: false },
        { fn: 'bowl', x: 21, w: 5, accent: false },
        { fn: 'plate', x: 28, w: 6, accent: false },
      ],
    ],
  },
  plates: {
    shelves: [
      [
        { fn: 'plate', x: 10.5, w: 7, accent: false },
        { fn: 'plate', x: 19, w: 6, accent: true },
        { fn: 'plate', x: 27, w: 7, accent: false },
      ],
      [
        { fn: 'bowl', x: 10.5, w: 6, accent: false },
        { fn: 'plate', x: 18, w: 5, accent: false },
        { fn: 'bowl', x: 24.5, w: 5, accent: false },
        { fn: 'plate', x: 31, w: 5, accent: false },
      ],
      [
        { fn: 'plate', x: 11, w: 5, accent: false },
        { fn: 'plate', x: 18, w: 6, accent: false },
        { fn: 'plate', x: 26, w: 5, accent: false },
        { fn: 'plate', x: 32, w: 4, accent: false },
      ],
    ],
  },
  mixed: {
    shelves: [
      [
        { fn: 'jar', x: 10.5, w: 3, h: 5, lid: true, accent: false },
        { fn: 'cheese', x: 15.5, r: 1.8, holes: true, accent: false },
        { fn: 'bottle', x: 20, h: 5.5, accent: false },
        { fn: 'jar', x: 24, w: 2.8, h: 4.2, lid: true, accent: true },
        { fn: 'cheese', x: 29, r: 1.6, holes: false, accent: false },
      ],
      [
        { fn: 'pot', x: 11, w: 3.5, h: 4.5, accent: false },
        { fn: 'jar', x: 16, w: 2.5, h: 4, lid: false, accent: false },
        { fn: 'cheese', x: 21, r: 2.2, holes: true, accent: false },
        { fn: 'bottle', x: 26, h: 5, accent: false },
        { fn: 'jar', x: 30, w: 2.5, h: 3.5, lid: true, accent: false },
      ],
      [
        { fn: 'plate', x: 13, w: 8, accent: false },
        { fn: 'bowl', x: 22, w: 5, accent: false },
        { fn: 'plate', x: 29, w: 5, accent: false },
      ],
    ],
  },
};

function renderItem(item: Item, y: number, fill: string, accent: string, key: number): React.ReactElement | React.ReactElement[] {
  const useFill = item.accent ? accent : fill;
  switch (item.fn) {
    case 'jar': {
      const x = item.x - item.w / 2;
      const top = y - item.h;
      const lidH = item.lid ? 0.8 : 0;
      return (
        <g key={key}>
          {item.lid && (
            <rect x={x - 0.4} y={top - lidH} width={item.w + 0.8} height={lidH} rx={0.2} fill={useFill} />
          )}
          <rect x={x} y={top} width={item.w} height={item.h} rx={0.6} fill={useFill} />
        </g>
      );
    }
    case 'cheese': {
      const cy = y - item.r;
      return (
        <g key={key}>
          <circle cx={item.x} cy={cy} r={item.r} fill={useFill} />
          {item.holes && (
            <>
              <circle cx={item.x - item.r * 0.3} cy={cy - item.r * 0.2} r={item.r * 0.18} fill="#FBF7EE" />
              <circle cx={item.x + item.r * 0.35} cy={cy + item.r * 0.15} r={item.r * 0.14} fill="#FBF7EE" />
              <circle cx={item.x + item.r * 0.05} cy={cy + item.r * 0.4} r={item.r * 0.10} fill="#FBF7EE" />
            </>
          )}
        </g>
      );
    }
    case 'pot': {
      const x = item.x - item.w / 2;
      const top = y - item.h;
      return (
        <g key={key}>
          <rect x={x} y={top + 1.2} width={item.w} height={item.h - 1.2} rx={0.8} fill={useFill} />
          <rect x={x - 0.3} y={top + 0.8} width={item.w + 0.6} height={0.7} rx={0.2} fill={useFill} />
          <rect x={item.x - 1.1} y={top - 1.6} width={0.5} height={2.6} rx={0.2} fill={useFill} />
          <rect x={item.x - 0.2} y={top - 2.2} width={0.5} height={3.2} rx={0.2} fill={useFill} />
          <rect x={item.x + 0.7} y={top - 1.2} width={0.5} height={2.4} rx={0.2} fill={useFill} />
        </g>
      );
    }
    case 'plate': {
      const h = 1.8;
      return (
        <g key={key}>
          <ellipse cx={item.x} cy={y - h / 2} rx={item.w / 2} ry={h / 2} fill={useFill} />
          <ellipse cx={item.x} cy={y - h / 2 - 0.25} rx={item.w / 2 - 0.6} ry={h / 2 - 0.4} fill={useFill} />
        </g>
      );
    }
    case 'bowl': {
      const x = item.x - item.w / 2;
      return (
        <path key={key} d={`M ${x} ${y - 2.6} Q ${item.x} ${y} ${x + item.w} ${y - 2.6} Z`} fill={useFill} />
      );
    }
    case 'bottle': {
      const w = 2.0;
      const x = item.x - w / 2;
      const top = y - item.h;
      const neckH = 1.6;
      return (
        <g key={key}>
          <rect x={item.x - 0.5} y={top} width={1} height={neckH} rx={0.2} fill={useFill} />
          <rect x={x} y={top + neckH} width={w} height={item.h - neckH} rx={0.6} fill={useFill} />
        </g>
      );
    }
  }
}

const RRLogo: React.FC<RRLogoProps> = ({
  shelf = 'mixed',
  accent: accentProp = '#C7553B',
  fill: fillProp = '#1F2024',
  stroke: strokeProp = '#1F2024',
  frame: frameProp = 'rounded',
  frameFill: frameFillProp = '#FBF7EE',
  frameStroke: frameStrokeProp = true,
  variant = 'default',
  size,
}) => {
  // Variant overrides (mirrors `renderAllLogos` in logo.js).
  let fill = fillProp;
  let stroke = strokeProp;
  let accent = accentProp;
  let frame = frameProp;
  let frameFill: string | null = frameFillProp;
  if (variant === 'reverse') {
    fill = '#FBF7EE';
    stroke = '#FBF7EE';
    frameFill = null;
  } else if (variant === 'icon') {
    frame = 'none';
  } else if (variant === 'monoaccent') {
    accent = fill;
  }

  const cfg = STYLES[shelf] ?? STYLES.mixed;
  const strokeW = 1;
  const sizeStyle = size === undefined
    ? { width: '100%', height: '100%' }
    : { width: size, height: size };

  return (
    <svg
      viewBox={`0 0 ${VB} ${VB}`}
      fill="none"
      preserveAspectRatio="xMidYMid meet"
      style={sizeStyle}
      aria-hidden="true"
    >
      {frame === 'rounded' && (
        <rect
          x={0.5}
          y={0.5}
          width={39}
          height={39}
          rx={9}
          fill={frameFill ?? 'none'}
          stroke={frameStrokeProp ? stroke : 'none'}
          strokeWidth={strokeW}
        />
      )}
      {SHELVES.map((y) => (
        <line
          key={`shelf-${y}`}
          x1={6}
          y1={y}
          x2={34}
          y2={y}
          stroke={stroke}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />
      ))}
      {cfg.shelves.flatMap((items, shelfIdx) =>
        items.map((item, itemIdx) => renderItem(item, SHELVES[shelfIdx], fill, accent, shelfIdx * 100 + itemIdx))
      )}
    </svg>
  );
};

export default RRLogo;
